import { kitchenApi } from './kitchenApi';
import { ApiError } from './errors';
import { publicGet } from './publicClient';
import type { MenuItemDto } from '../api-types';
import type { Category } from '../api-types/models/Category';
import type { Tax } from '../api-types/models/Tax';
import type { WeeklyMenuResponse } from './menu/types';

export const menuApi = kitchenApi.injectEndpoints({
    endpoints: (builder) => ({
        getMenu: builder.query<MenuItemDto[], string>({
            query: (dateIso) => ({
                apiName: 'brunch-v2',
                path: `/menu/${dateIso}`,
                method: 'GET'
            }),
            providesTags: (_result, _error, dateIso) => [
                { type: 'Menu', id: dateIso }
            ]
        }),

        getGroups: builder.query<Category[], void>({
            query: () => ({
                apiName: 'brunch-v2',
                path: '/menu/groups',
                method: 'GET'
            }),
            providesTags: ['Menu']
        }),

        getCategories: builder.query<Category[], void>({
            query: () => ({
                apiName: 'brunch-v2',
                path: '/menu/categories',
                method: 'GET'
            }),
            providesTags: ['Menu']
        }),

        getAllergens: builder.query<Category[], void>({
            query: () => ({
                apiName: 'brunch-v2',
                path: '/menu/allergens',
                method: 'GET'
            }),
            providesTags: ['Menu']
        }),

        getTaxOptions: builder.query<Tax, void>({
            query: () => ({
                apiName: 'brunch-v2',
                path: '/menu/tax',
                method: 'GET'
            }),
            providesTags: ['Menu']
        }),

        deleteMenuItem: builder.mutation<
            boolean,
            { id: string; uuid: string; dateIso: string }
        >({
            query: ({ id, uuid }) => ({
                apiName: 'brunch-v2',
                path: `/menu/offer/${id}/${uuid}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_result, _error, { dateIso }) => [
                { type: 'Menu', id: dateIso }
            ]
        }),

        uploadMenuItemPhoto: builder.mutation<
            MenuItemDto,
            { uuid: string; formData: FormData }
        >({
            query: ({ uuid, formData }) => ({
                apiName: 'brunch-v2',
                path: `/menu/${uuid}/image`,
                method: 'POST',
                body: formData
            }),
            invalidatesTags: ['Menu', 'Registry']
        }),

        deleteMenuItemPhoto: builder.mutation<MenuItemDto, { uuid: string }>({
            query: ({ uuid }) => ({
                apiName: 'brunch-v2',
                path: `/menu/${uuid}/image`,
                method: 'DELETE'
            }),
            invalidatesTags: ['Menu', 'Registry']
        }),

        publishMenuItems: builder.mutation<
            MenuItemDto[],
            { date?: string; menuItems: string[] }
        >({
            query: (body) => ({
                apiName: 'brunch-v2',
                path: '/menu/offer',
                method: 'POST',
                body
            }),
            invalidatesTags: ['Menu']
        }),

        uploadMenu: builder.mutation<MenuItemDto[], FormData>({
            query: (formData) => ({
                apiName: 'brunch',
                path: '/menu/items',
                method: 'POST',
                body: formData
            }),
            invalidatesTags: ['Menu']
        }),

        getWeeklyMenu: builder.query<WeeklyMenuResponse, string>({
            queryFn: async (date) => {
                try {
                    const base = process.env.REACT_APP_REDIRECT_URL ?? '';
                    const data = await publicGet<WeeklyMenuResponse>(
                        `${base}/api/v3/menu/${date}/week`
                    );
                    return { data };
                } catch (error) {
                    return {
                        error:
                            error instanceof ApiError
                                ? error
                                : new ApiError(
                                      500,
                                      error instanceof Error
                                          ? error.message
                                          : 'Unknown error'
                                  )
                    };
                }
            },
            providesTags: ['Menu']
        })
    }),
    overrideExisting: true
});

export const {
    useGetMenuQuery,
    useLazyGetMenuQuery,
    useGetGroupsQuery,
    useGetCategoriesQuery,
    useGetAllergensQuery,
    useGetTaxOptionsQuery,
    useDeleteMenuItemMutation,
    useUploadMenuItemPhotoMutation,
    useDeleteMenuItemPhotoMutation,
    usePublishMenuItemsMutation,
    useUploadMenuMutation,
    useGetWeeklyMenuQuery
} = menuApi;
